import { useEffect, useState } from 'react';

/**
 * Block 12 PR-D-B Phase G1 — reactive `matchMedia` wrapper.
 *
 * Используется в `ShareBottomSheet` для responsive переключения mobile/desktop
 * UI (см. PR-D-B § «Responsive UI through CSS media query»). Tailwind `md`
 * breakpoint = 768px, поэтому запрос всегда вида `(min-width: 768px)`.
 *
 * SSR / jsdom-fallback: если `window.matchMedia` отсутствует — возвращаем
 * `false` (mobile UI по умолчанию). Существующие mobile-тесты не требуют
 * мока matchMedia.
 *
 * Реактивный listener подписывается на `change` event. Re-sync после
 * подписки закрывает гонку между initial `useState` и mount listener
 * (matchMedia мог измениться между этими тиками).
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => detectMatches(query));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);

    mql.addEventListener('change', handler);
    setMatches(mql.matches);

    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

function detectMatches(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(query).matches;
}
